import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faFlag,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faCalculator,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../../Components/Common/Modal";
import { useEffect, useState, useRef } from "react";
import Button from "../../../../Components/Common/Button";
import useSWR from "swr";
import { modelTestUrls } from "../../../../Api/Auth/ApiUrls";
import { customFetcher } from "../../../../Api/swrConfig";
import { useAuth } from "../../../../Context/AuthContext";

// Floating Calculator Component
const FloatingCalculator = ({ isVisible, onClose }) => {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForNewValue, setWaitingForNewValue] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const calculatorRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.closest(".calc-button") || e.target.closest(".calc-close"))
      return;
    const rect = calculatorRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const inputNumber = (num) => {
    if (waitingForNewValue) {
      setDisplay(String(num));
      setWaitingForNewValue(false);
    } else {
      setDisplay(display === "0" ? String(num) : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForNewValue) {
      setDisplay("0.");
      setWaitingForNewValue(false);
    } else if (display.indexOf(".") === -1) {
      setDisplay(display + ".");
    }
  };

  const clear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(false);
  };

  const performOperation = (nextOperation) => {
    const inputValue = parseFloat(display);
    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);
      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }
    setWaitingForNewValue(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue, secondValue, operation) => {
    switch (operation) {
      case "+":
        return firstValue + secondValue;
      case "-":
        return firstValue - secondValue;
      case "×":
        return firstValue * secondValue;
      case "÷":
        return firstValue / secondValue;
      case "=":
        return secondValue;
      default:
        return secondValue;
    }
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);
    if (previousValue !== null && operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForNewValue(true);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      ref={calculatorRef}
      className="fixed bg-white border-2 border-gray-300 rounded-lg shadow-2xl z-[10000] select-none"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="flex items-center justify-between bg-gray-100 p-2 rounded-t-lg">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faGripVertical} className="text-gray-400" />
          <span className="text-sm font-medium">Calculator</span>
        </div>
        <button
          className="calc-close text-gray-500 hover:text-red-600 w-6 h-6 flex items-center justify-center"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} size="sm" />
        </button>
      </div>
      <div className="p-4 pb-2">
        <div className="bg-gray-900 text-white text-right p-3 rounded text-xl font-mono min-h-[50px] flex items-center justify-end">
          {display}
        </div>
      </div>
      <div className="p-4 pt-2">
        <div className="grid grid-cols-4 gap-2">
          <button
            className="calc-button bg-gray-200 hover:bg-gray-300 p-3 rounded text-sm font-medium"
            onClick={clear}
          >
            C
          </button>
          <button className="calc-button bg-gray-200 hover:bg-gray-300 p-3 rounded text-sm font-medium">
            ±
          </button>
          <button className="calc-button bg-gray-200 hover:bg-gray-300 p-3 rounded text-sm font-medium">
            %
          </button>
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("÷")}
          >
            ÷
          </button>
          {[7, 8, 9].map((num) => (
            <button
              key={num}
              className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
              onClick={() => inputNumber(num)}
            >
              {num}
            </button>
          ))}
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("×")}
          >
            ×
          </button>
          {[4, 5, 6].map((num) => (
            <button
              key={num}
              className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
              onClick={() => inputNumber(num)}
            >
              {num}
            </button>
          ))}
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("-")}
          >
            -
          </button>
          {[1, 2, 3].map((num) => (
            <button
              key={num}
              className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
              onClick={() => inputNumber(num)}
            >
              {num}
            </button>
          ))}
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("+")}
          >
            +
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium col-span-2"
            onClick={() => inputNumber(0)}
          >
            0
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={inputDecimal}
          >
            .
          </button>
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={handleEquals}
          >
            =
          </button>
        </div>
      </div>
    </div>
  );
};

const FreeSimulatorModal = ({ selected, isOpen, onClose }) => {
  const { token } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExamSubmitted, setIsExamSubmitted] = useState(false);
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    src: "",
    alt: "",
  });
  const [calculatorVisible, setCalculatorVisible] = useState(false);
  const [resultModal, setResultModal] = useState(false);

  const { data: questionsData, isLoading: isLoadingQuestions } = useSWR(
    selected && isOpen
      ? modelTestUrls.getSingleFreeModelTestTaker(selected._id)
      : null,
    (url) => customFetcher(url, token),
  );

  const { data: answersData } = useSWR(
    selected?._id && isExamSubmitted
      ? `${modelTestUrls.getSingleFreeModelTestTaker(
          selected._id,
          "answer=true",
        )}`
      : null,
    (url) => customFetcher(url, token),
  );

  const questions = questionsData?.data || [];
  const questionsWithAnswers = answersData?.data || [];

  useEffect(() => {
    if (!isOpen || !selected?.duration) return;
    const parts = selected.duration.split(":").map(Number);
    let totalSeconds = 0;
    if (parts.length === 2) {
      totalSeconds = parts[0] * 3600 + parts[1] * 60;
    } else if (parts.length === 3) {
      totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    setTimeRemaining(totalSeconds);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setIsExamSubmitted(false);
  }, [isOpen, selected?.duration]);

  useEffect(() => {
    if (!isOpen || timeRemaining <= 0 || isExamSubmitted) return;
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isOpen, timeRemaining, isExamSubmitted]);

  const handleOptionSelect = (optionIndex) => {
    if (isExamSubmitted) return;
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: optionIndex,
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleQuestionJump = (questionIndex) => {
    setCurrentQuestionIndex(questionIndex);
  };

  const handleSubmitExam = () => {
    setIsExamSubmitted(true);
    setResultModal(true);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const calculateScore = () => {
    if (!isExamSubmitted || !questionsWithAnswers.length) {
      return { correct: 0, wrong: 0, unanswered: questions.length };
    }
    let correct = 0,
      wrong = 0,
      unanswered = 0;
    questionsWithAnswers.forEach((question, index) => {
      const userAnswer = selectedAnswers[index];
      if (userAnswer === undefined) {
        unanswered++;
      } else if (userAnswer === parseInt(question.correctOptionIndex)) {
        correct++;
      } else {
        wrong++;
      }
    });
    return { correct, wrong, unanswered };
  };

  const getOptionClass = (optionIndex) => {
    const currentQuestion = isExamSubmitted
      ? questionsWithAnswers[currentQuestionIndex]
      : questions[currentQuestionIndex];
    const selectedOption = selectedAnswers[currentQuestionIndex];
    if (isExamSubmitted && questionsWithAnswers.length) {
      const correctIndex = parseInt(currentQuestion?.correctOptionIndex);
      if (optionIndex === correctIndex) return "bg-green-100 border-green-500";
      if (optionIndex === selectedOption && optionIndex !== correctIndex)
        return "bg-red-100 border-red-500";
      return "bg-gray-50";
    }
    return selectedOption === optionIndex
      ? "bg-blue-100 border-blue-500"
      : "bg-white hover:bg-gray-50";
  };

  const getQuestionStatus = (questionIndex) => {
    if (isExamSubmitted && questionsWithAnswers.length) {
      const userAnswer = selectedAnswers[questionIndex];
      if (userAnswer !== undefined) {
        const isCorrect =
          userAnswer ===
          parseInt(questionsWithAnswers[questionIndex]?.correctOptionIndex);
        return isCorrect ? "correct" : "incorrect";
      }
      return "unanswered";
    }
    return selectedAnswers[questionIndex] !== undefined
      ? "answered"
      : "unanswered";
  };

  const getPaginationButtonClass = (questionIndex) => {
    const status = getQuestionStatus(questionIndex);
    const isActive = questionIndex === currentQuestionIndex;
    let baseClass =
      "w-8 h-8 rounded-full text-sm font-medium transition-all cursor-pointer flex items-center justify-center ";
    if (isActive) baseClass += "ring-2 ring-blue-500 ring-offset-2 ";
    if (isExamSubmitted) {
      switch (status) {
        case "correct":
          return baseClass + "bg-green-500 text-white hover:bg-green-600";
        case "incorrect":
          return baseClass + "bg-red-500 text-white hover:bg-red-600";
        default:
          return baseClass + "bg-gray-200 text-gray-700 hover:bg-gray-300";
      }
    }
    return status === "answered"
      ? baseClass + "bg-blue-500 text-white hover:bg-blue-600"
      : baseClass + "bg-gray-200 text-gray-700 hover:bg-gray-300";
  };

  const handleImageClick = (src, alt) => {
    setImagePopup({ isOpen: true, src, alt });
  };

  const closeImagePopup = () => {
    setImagePopup({ isOpen: false, src: "", alt: "" });
  };

  const handleClose = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setIsExamSubmitted(false);
    setTimeRemaining(0);
    setCalculatorVisible(false);
    onClose();
  };

  const currentQuestion = isExamSubmitted
    ? questionsWithAnswers[currentQuestionIndex]
    : questions[currentQuestionIndex];
  const selectedOption = selectedAnswers[currentQuestionIndex];
  const { correct, wrong, unanswered } = calculateScore();

  if (!isOpen) return null;

  if (isLoadingQuestions) {
    return (
      <Modal
        isOpen={isOpen}
        title="Loading Exam..."
        footer={null}
        onCancel={handleClose}
        formWidth="max-w-md"
        formHeight="max-h-64"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3">Loading exam...</span>
        </div>
      </Modal>
    );
  }

  return (
    <>
      {imagePopup.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[20000]"
          onClick={closeImagePopup}
        >
          <div className="relative max-w-7xl max-h-[90vh] p-4">
            <button
              className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75"
              onClick={closeImagePopup}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <img
              src={imagePopup.src}
              alt={imagePopup.alt}
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      <FloatingCalculator
        isVisible={calculatorVisible}
        onClose={() => setCalculatorVisible(false)}
      />
      {isExamSubmitted && resultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[15000]">
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon
                    icon={faFlag}
                    className="text-blue-600 text-2xl"
                  />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Exam Submitted!
                </h2>
                <p className="text-gray-600 mb-6">Your exam has ended</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="text-5xl font-bold text-blue-600 mb-2">
                  {correct}/{questions.length}
                </div>
                <p className="text-gray-600 text-sm">Total Score</p>

                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {correct}
                    </div>
                    <div className="text-xs text-gray-500">Correct</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">
                      {wrong}
                    </div>
                    <div className="text-xs text-gray-500">Wrong</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-600">
                      {unanswered}
                    </div>
                    <div className="text-xs text-gray-500">Skipped</div>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => setResultModal(false)}
                variant="primary"
                className="w-full"
              >
                View Solutions
              </Button>
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={isOpen}
        title={`${selected?.title || "Free Simulator"} - Question ${
          currentQuestionIndex + 1
        }`}
        footer={null}
        onCancel={handleClose}
        formWidth="max-w-[98%]"
        formHeight="max-h-[90vh]"
      >
        <div className="h-full flex flex-col">
          <div className="flex justify-between items-center bg-blue-50 px-6 py-3 border-b">
            <div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <FontAwesomeIcon icon={faClock} />
                  <span
                    className={`font-mono ${
                      timeRemaining < 300 ? "text-red-600 font-bold" : ""
                    }`}
                  >
                    {formatTime(timeRemaining)}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </div>
                <div className="text-sm text-green-600 font-medium">
                  FREE TEST
                </div>
              </div>
              {isExamSubmitted && (
                <div className="text-sm font-medium mt-2">
                  <span className="text-green-600">Correct: {correct}</span>
                  <span className="mx-2 text-gray-400">|</span>
                  <span className="text-red-600">Wrong: {wrong}</span>
                  <span className="mx-2 text-gray-400">|</span>
                  <span className="text-gray-600">
                    Unanswered: {unanswered}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                className="text-blue-600 hover:text-blue-800 flex items-center gap-2 text-sm"
                onClick={() => setCalculatorVisible(!calculatorVisible)}
              >
                <FontAwesomeIcon icon={faCalculator} />
                Calculator
              </button>
              {/* <button className="text-red-600 hover:text-red-800 flex items-center gap-2 text-sm">
                <FontAwesomeIcon icon={faFlag} />
                Report Issue
              </button> */}
              {!isExamSubmitted && (
                <Button
                  onClick={handleSubmitExam}
                  variant="danger"
                  size="small"
                  disabled={Object.keys(selectedAnswers).length === 0}
                >
                  Submit Exam
                </Button>
              )}
            </div>
          </div>
          <div className="px-3 py-3 flex justify-between items-center my-2">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-4 flex-wrap">
                {questions.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionJump(index)}
                    className={getPaginationButtonClass(index)}
                    title={`Question ${index + 1} - ${getQuestionStatus(
                      index,
                    )}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                variant="outline"
                size="small"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
                Previous
              </Button>
              <Button
                onClick={handleNext}
                disabled={currentQuestionIndex === questions.length - 1}
                variant="outline"
                size="small"
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
              </Button>
            </div>
          </div>
          <div className="flex-1 flex overflow-auto">
            <div className="flex-1 p-3">
              <div className="max-w-5xl">
                <div
                  className="mb-3 text-gray-800"
                  dangerouslySetInnerHTML={{
                    __html: currentQuestion?.questionText || "",
                  }}
                />
                {currentQuestion?.questionImage && (
                  <div className="mb-3">
                    <img
                      src={currentQuestion.questionImage}
                      alt="Question"
                      className="w-[850px] h-auto rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() =>
                        handleImageClick(
                          currentQuestion.questionImage,
                          "Question Image",
                        )
                      }
                    />
                  </div>
                )}
                <div className="mb-6 mt-3">
                  <div className="grid grid-cols-2 gap-4">
                    {currentQuestion?.options?.map((option, index) => (
                      <label
                        key={index}
                        className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${getOptionClass(
                          index + 1,
                        )} ${isExamSubmitted ? "cursor-not-allowed" : ""}`}
                        onClick={() =>
                          !isExamSubmitted && handleOptionSelect(index + 1)
                        }
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name={`question_${currentQuestionIndex}`}
                            checked={selectedOption === index + 1}
                            onChange={() =>
                              !isExamSubmitted && handleOptionSelect(index + 1)
                            }
                            disabled={isExamSubmitted}
                            className="mr-3"
                          />
                          <span className="font-medium mr-2">
                            {String.fromCharCode(65 + index)}.
                          </span>
                          <div className="flex flex-col gap-2 flex-1">
                            {index === 0 && currentQuestion?.optionAImage && (
                              <img
                                src={currentQuestion.optionAImage}
                                alt="Option A"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionAImage,
                                    "Option A",
                                  );
                                }}
                              />
                            )}
                            {index === 1 && currentQuestion?.optionBImage && (
                              <img
                                src={currentQuestion.optionBImage}
                                alt="Option B"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionBImage,
                                    "Option B",
                                  );
                                }}
                              />
                            )}
                            {index === 2 && currentQuestion?.optionCImage && (
                              <img
                                src={currentQuestion.optionCImage}
                                alt="Option C"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionCImage,
                                    "Option C",
                                  );
                                }}
                              />
                            )}
                            {index === 3 && currentQuestion?.optionDImage && (
                              <img
                                src={currentQuestion.optionDImage}
                                alt="Option D"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionDImage,
                                    "Option D",
                                  );
                                }}
                              />
                            )}
                            <div className="flex items-center justify-between">
                              <span>{option}</span>
                              {isExamSubmitted && (
                                <div className="flex items-center gap-2 ml-2">
                                  {selectedOption === index + 1 && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      Your Answer
                                    </span>
                                  )}
                                  {parseInt(
                                    currentQuestion?.correctOptionIndex,
                                  ) ===
                                    index + 1 && (
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                      Correct
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {isExamSubmitted && questionsWithAnswers.length > 0 && (
              <div className="w-[50%] bg-gray-50 border-l p-3">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">Solution</h3>
                </div>
                <div className="mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg mb-2">
                    <span className="font-semibold text-blue-800">
                      Detailed Solution
                    </span>
                  </div>
                  {currentQuestion?.solutionText && (
                    <div
                      className="mb-4 text-gray-700"
                      dangerouslySetInnerHTML={{
                        __html: currentQuestion.solutionText,
                      }}
                    />
                  )}
                  {currentQuestion?.solutionImage &&
                    currentQuestion.solutionImage.length > 0 && (
                      <div className="flex flex-col gap-3 mb-4">
                        {currentQuestion.solutionImage.map((item, index) =>
                          item ? (
                            <img
                              key={index}
                              src={item}
                              alt="Solution"
                              className="w-full h-auto rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() =>
                                handleImageClick(item, "Solution Image")
                              }
                            />
                          ) : (
                            <></>
                          ),
                        )}
                      </div>
                    )}
                  <div className="text-sm">
                    <div className="font-medium mb-2">Answer Analysis:</div>
                    <div className="bg-gray-100 p-3 rounded-lg space-y-2">
                      <div className="flex justify-between items-center">
                        <span>Correct Answer:</span>
                        <span className="font-medium text-green-600">
                          (
                          {String.fromCharCode(
                            65 +
                              parseInt(currentQuestion?.correctOptionIndex - 1),
                          )}
                          ){" "}
                          {
                            currentQuestion?.options?.[
                              parseInt(currentQuestion?.correctOptionIndex) - 1
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Your Answer:</span>
                        {selectedOption !== undefined ? (
                          <span
                            className={`font-medium ${
                              selectedOption ===
                              parseInt(currentQuestion?.correctOptionIndex)
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            ({String.fromCharCode(65 + selectedOption - 1)}){" "}
                            {currentQuestion?.options?.[selectedOption - 1]}
                          </span>
                        ) : (
                          <span className="text-gray-500 italic">
                            Not answered
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span>Result:</span>
                        <span
                          className={`font-bold ${
                            selectedOption ===
                            parseInt(currentQuestion?.correctOptionIndex)
                              ? "text-green-600"
                              : selectedOption !== undefined
                                ? "text-red-600"
                                : "text-gray-600"
                          }`}
                        >
                          {selectedOption ===
                          parseInt(currentQuestion?.correctOptionIndex)
                            ? "✓ Correct"
                            : selectedOption !== undefined
                              ? "✗ Incorrect"
                              : "- Not Answered"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FreeSimulatorModal;
